import React, { useState } from 'react';
import {
  Card,
  Row,
  Timeline,
  Col,
  Tooltip,
  Avatar,
  Modal,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import moment from 'moment';
import {
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  FileImageOutlined,
  ArrowDownOutlined,
  ClockCircleOutlined,
  GlobalOutlined,
  LinkOutlined,
} from '@ant-design/icons';
const { Item } = Timeline;
const { Text, Title } = Typography;

const COLORS = {
  impression: '#1890ff',
  journey: '#52c41a',
  background: '#fafafa',
};

const styles = {
  card: {
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  labelText: {
    fontSize: 11,
    textTransform: 'uppercase',
  },
  ellipsisContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  arrowDot: {
    background: COLORS.background,
    margin: '-1px -3px -3px -1px',
  },
};

const SessionJourneyExpand = ({ impressions = [], journey = [], loading = false }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentImpression, setCurrentImpression] = useState('');
  const showModal = (impression) => {
    if (impression.media) {
      setIsModalVisible(true);
      setCurrentImpression(impression);
    }
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  if (loading) {
    return (
      <Card
        bodyStyle={{
          overflowY: 'auto',
          height: '500px',
          padding: '24px',
        }}
      >
        <Timeline>
          <Item>
            <Title level={5} style={{ margin: 0, color: COLORS.impression }}>
              Loading Impression Journey...
            </Title>
          </Item>
          {[1, 2].map((i) => (
            <Item
              key={i}
              dot={
                <div style={styles.arrowDot}>
                  <ArrowDownOutlined style={{ fontSize: 12 }} />
                </div>
              }
            >
              <Card
                size="small"
                style={styles.card}
              >
                <Row gutter={[24, 0]} align="middle">
                  <Col flex="none">
                    <Skeleton.Avatar active size={64} shape="square" />
                  </Col>
                  <Col flex="1">
                    <Skeleton.Input active size="small" style={{ width: '80%' }} />
                  </Col>
                  <Col flex="1">
                    <Skeleton.Input active size="small" style={{ width: '90%' }} />
                  </Col>
                  <Col flex="1">
                    <Skeleton.Input active size="small" style={{ width: '85%' }} />
                  </Col>
                  <Col flex="1">
                    <Skeleton.Input active size="small" style={{ width: '75%' }} />
                  </Col>
                </Row>
              </Card>
            </Item>
          ))}
          <Item>
            <Title level={5} style={{ margin: 0, color: COLORS.journey }}>
              Loading Session Journey...
            </Title>
          </Item>
          {[1, 2].map((i) => (
            <Item
              key={`journey-${i}`}
              color={COLORS.journey}
              dot={
                <div style={styles.arrowDot}>
                  <ArrowDownOutlined style={{ fontSize: 12 }} />
                </div>
              }
            >
              <Card
                size="small"
                style={styles.card}
              >
                <Row gutter={[16, 12]}>
                  <Col span={8}>
                    <Skeleton.Input active size="small" style={{ width: '100%' }} />
                  </Col>
                  <Col span={8}>
                    <Skeleton.Input active size="small" style={{ width: '100%' }} />
                  </Col>
                  <Col span={8}>
                    <Skeleton.Input active size="small" style={{ width: '100%' }} />
                  </Col>
                </Row>
              </Card>
            </Item>
          ))}
        </Timeline>
      </Card>
    );
  }

  const makeTimeLine = impressions.map((impression, i) => {
    const {
      impressionTime,
      impressionPubliserViewed,
      impressionPublisher,
      impressionPublisherPlatform,
      impressionPublisherUrl,
    } = impression;

    // TODO: add logic to determine application platform
    let appTypeIcon = () => {
      return <DesktopOutlined />;
    };

    let icon = () => {
      if (
        impressionPubliserViewed === 'Unknown' ||
        impressionPubliserViewed === 'Chromebook Computer' ||
        impressionPubliserViewed === 'Windows Computer' ||
        impressionPubliserViewed === 'Computer'
      ) {
        return <DesktopOutlined />;
      }

      if (
        impressionPubliserViewed === 'iPhone' ||
        impressionPubliserViewed === 'Android' ||
        impressionPubliserViewed === 'Windows Phone'
      ) {
        return <MobileOutlined />;
      }

      if (impressionPubliserViewed === 'iPad') {
        return <TabletOutlined />;
      }
    };

    return (
      <Item
        key={i}
        dot={
          <div style={styles.arrowDot}>
            <ArrowDownOutlined style={{ fontSize: 12 }} />
          </div>
        }
      >
        <Card
          size="small"
          style={styles.card}
          bodyStyle={{ padding: '16px' }}
        >
          <Row gutter={[24, 0]} align="middle">
            <Col flex="none">
              <Tooltip title="Click to view full image">
                <Avatar
                  shape="square"
                  alt={impression.media}
                  size={64}
                  icon={<FileImageOutlined />}
                  onClick={() => showModal(impression)}
                  src={impression.media === '' || !impression.media ? false : impression.media}
                  style={{
                    cursor: impression.media ? 'pointer' : 'default',
                    border: '1px solid #f0f0f0',
                    boxShadow: impression.media ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                  }}
                />
              </Tooltip>
            </Col>
            <Col flex="1">
              <Space direction="vertical" size={2}>
                <Text strong style={styles.valueText}>
                  <ClockCircleOutlined style={{ marginRight: 6 }} />
                  Impression Served
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {moment(impressionTime, moment.DATETIME_LOCAL_MS).format(
                    'MM/DD/YY HH:mm:ss.SSSZ'
                  )}
                </Text>
              </Space>
            </Col>
            <Col flex="1">
              <Space direction="vertical" size={2}>
                <Text type="secondary" style={styles.labelText}>
                  Device
                </Text>
                <Space size="small">
                  {icon()}
                  <Text strong style={styles.valueText}>
                    {impressionPubliserViewed || 'Unknown'}
                  </Text>
                </Space>
              </Space>
            </Col>
            <Col flex="1">
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text type="secondary" style={styles.labelText}>
                  Served On
                </Text>
                <Space size="small" align="start" style={{ width: '100%', overflow: 'hidden' }}>
                  {impression.impressionPublisherMedia && (
                    <Avatar
                      shape="square"
                      alt={impression.impressionPublisherMedia}
                      size={20}
                      icon={<FileImageOutlined />}
                      src={impression.impressionPublisherMedia}
                      style={{ flexShrink: 0 }}
                    />
                  )}
                  <Tooltip title={impressionPublisher}>
                    <div
                      style={{
                        ...styles.ellipsisContainer,
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Text strong>
                        {impressionPublisher || 'Unknown'}
                      </Text>
                    </div>
                  </Tooltip>
                </Space>
              </Space>
            </Col>
            <Col flex="1">
              <Space direction="vertical" size={2}>
                <Text type="secondary" style={styles.labelText}>
                  Platform
                </Text>
                <Space size="small">
                  {appTypeIcon()}
                  <Text strong>
                    {impressionPublisherPlatform || 'Unknown'}
                  </Text>
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>
      </Item>
    );
  });

  return (
    <Card
      bodyStyle={{
        overflowY: 'auto',
        height: '500px',
        padding: '24px',
        background: COLORS.background,
      }}
    >
      <Modal
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
        width={800}
        centered
        title={
          <Space size="middle">
            <Text strong style={{ fontSize: 16 }}>
              Impression
            </Text>
          </Space>
        }
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <img
            src={currentImpression.media}
            alt={currentImpression.media}
            style={{
              width: '100%',
              display: 'block',
              borderRadius: 4,
            }}
          />
          {currentImpression.impressionPublisherUrl && (
            <Card size="small" style={{ background: COLORS.background }}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text type="secondary" style={styles.labelText}>
                  <LinkOutlined style={{ marginRight: 4 }} />
                  Click Through URL
                </Text>
                <a
                  href={currentImpression.impressionPublisherUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    wordBreak: 'break-all',
                    color: COLORS.impression,
                    fontSize: 13,
                  }}
                >
                  {currentImpression.impressionPublisherUrl}
                </a>
              </Space>
            </Card>
          )}
        </Space>
      </Modal>
      <Timeline>
        <Item>
          <Title level={5} style={{ margin: 0, color: COLORS.impression }}>
            Impression Journey
          </Title>
        </Item>
        {makeTimeLine}
        <Item color={COLORS.journey}>
          <Title level={5} style={{ margin: 0, color: COLORS.journey }}>
            Session Journey
          </Title>
        </Item>
        {journey
          .filter((event) => event.EVENT !== 'record')
          .map((event, idx) => {
            return (
              <Item
                key={idx}
                color={COLORS.journey}
                dot={
                  <div style={styles.arrowDot}>
                    <ArrowDownOutlined style={{ fontSize: 12 }} />
                  </div>
                }
              >
                <Card
                  size="small"
                  style={{
                    ...styles.card,
                    background: '#fff',
                  }}
                  bodyStyle={{ padding: '12px 16px' }}
                >
                  <Row gutter={[16, 12]}>
                    <Col span={8}>
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={styles.labelText}>
                          <ClockCircleOutlined style={{ marginRight: 4 }} />
                          Timestamp
                        </Text>
                        <Text strong style={styles.valueText}>
                          {moment(event.dateTime).format('YYYY-MM-DD HH:mm:ss')}
                        </Text>
                      </Space>
                    </Col>
                    <Col span={8}>
                      <Space direction="vertical" size={2}>
                        <Text type="secondary" style={styles.labelText}>
                          Event Type
                        </Text>
                        <Text strong style={{ ...styles.valueText, color: COLORS.journey }}>
                          {event.event || 'Unknown'}
                        </Text>
                      </Space>
                    </Col>
                    <Col span={8}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        <Text type="secondary" style={styles.labelText}>
                          <GlobalOutlined style={{ marginRight: 4 }} />
                          URL
                        </Text>
                        <Tooltip title={event.url}>
                          <div
                            style={{
                              ...styles.ellipsisContainer,
                              maxWidth: '100%',
                            }}
                          >
                            <Text strong style={styles.valueText}>
                              {event.url || 'N/A'}
                            </Text>
                          </div>
                        </Tooltip>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Item>
            );
          })}
      </Timeline>
    </Card>
  );
};

export default SessionJourneyExpand;
